import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';

import useToggle from '../lib/hooks/useToggle';
import aspectRatio from '../lib/aspectRatio';
import { getVimeoVideo, selectVimeoSource } from '../lib/vimeoVideo';

import AnimateIn from './AnimateIn';
import Caption from './Caption';
import StoryblokEditableComment from './storyblok/StoryblokEditableComment';
import formatImageUrl from '../lib/image-format';

const VimeoVideo = ({ children, component, className, fullWidth = true }) => {
  // Only load poster video until play button his hit..?
  // Some kind of lazy loading is probably needed..?

  const [videoSource, setVideoSource] = useState();
  const [videoIsLoaded, setVideoIsLoaded] = useState(false);
  const [posterVideoSource, setPosterVideoSource] = useState();
  const [posterVideoIsLoaded, setPosterVideoIsLoaded] = useState(false);

  const [videoIsPlaying, togglePlay] = useToggle(false);
  const plyrVideoEl = useRef(null);
  const posterVideoEl = useRef(null);
  const [aspectPadding, setAspectPadding] = useState();

  const fullBleed = 'fullBleed' in component ? component.fullBleed : fullWidth; // fix for static case studies that don't define full_bleed by default
  const noBottomMargin = 'no_margin' in component ? component.no_margin : false

  useEffect(() => {
    if (videoIsPlaying) {
      plyrVideoEl.current.play();
    }
  }, [videoIsPlaying]);

  // Component did Mount
  useEffect(() => {
    const abortController = new AbortController();
    const signal = abortController.signal;

    // !component.calculateRatio : use default 16,9
    // component.calculateRatio = 'auto' : set aspectRatio based on the fetched video
    // component.calculateRatio = [w,h] : set aspectRatio based on pre-defined width & height
    if (!component.calculateRatio) {
      setAspectPadding(aspectRatio(16, 9));
    } else if (component.calculateRatio != 'auto') {
      let sizes = component.calculateRatio.split(',');
      setAspectPadding(aspectRatio(...sizes));
    }

    // get the posterVideo then set posterVideoSource
    if (component.posterVideo) {
      if (component.posterVideo.files) {
        posterVideoFiles(component.posterVideo.files);
      } else {
        getVimeoVideo(component.posterVideo, signal).then((res) => {
          if (!res.aborted) {
            posterVideoFiles(res.files);
          }
        });
      }

      function posterVideoFiles(files) {
        // if there is no video then set the asptect ratio based on the posterVideo
        if (!component.video && component.calculateRatio == 'auto') {
          setAspectPadding(aspectRatio(files[0].width, files[0].height));
        }
        setPosterVideoSource(selectVimeoSource(files));
        posterVideoEl.current.setAttribute('muted', 1);
        posterVideoEl.current.addEventListener('loadeddata', (e) => {
          setPosterVideoIsLoaded(true);
          //posterVideoEl.current.removeEventListener('loadeddata', e => {});
          e.target.removeEventListener('loadeddata', (e) => {});
        });
      }
    }

    // get the video then set videoSource
    if (component.video) {
      if (component.video.files) {
        videoFiles(component.video.files);
      } else {
        getVimeoVideo(component.video, signal).then((res) => {
          if (!res.aborted) {
            videoFiles(res.files);
          }
        });
      }

      function videoFiles(files) {
        // I think it's cleaner to always use posterVideo for autoplaying movies. So console warn about it.
        if (component.autoplay) {
          console.warn('For autoplaying VimeoVideo set posterVideo only');
        } else {
          // auto aspect?
          if (component.calculateRatio == 'auto') {
            setAspectPadding(aspectRatio(files[0].width, files[0].height));
          }
          setVideoSource(selectVimeoSource(files));

          plyrVideoEl.current.addEventListener('loadeddata', (e) => {
            setVideoIsLoaded(true);
            plyrVideoEl.current.removeEventListener('loadeddata', (e) => {});
          });
        }
      }
    }

    // cleanup
    return () => {
      abortController.abort();
    };
  }, []);

  let posterImage =
    typeof component.posterImage == 'object'
      ? component.posterImage.filename
      : component.posterImage;

  return (
    <AnimateIn
      className={classNames('video', {
        'g-row': !fullBleed,
        'video--playing': videoIsPlaying,
        'video--loaded': videoIsLoaded,
        'component--no-margin' : noBottomMargin
      })}
    >
      {component._editable && (
        <StoryblokEditableComment text={component._editable} />
      )}
      <div
        className="video__inner"
        style={{ paddingBottom: `${aspectPadding}%` }}
      >
        {component.video && !component.autoplay && (
          <video
            controls
            ref={plyrVideoEl}
            className={classNames('video', {
              'has-poster': component.posterVideo || posterImage,
            })}
            src={videoSource}
          />
        )}

        {
          // Video poster
          component.posterVideo && !videoIsPlaying && (
            <div
              className={classNames('video__poster', {
                'video__poster--loaded': posterVideoIsLoaded,
              })}
            >
              <video
                src={posterVideoSource}
                controls={false}
                preload="auto"
                great="potate"
                autoPlay
                loop
                muted
                playsInline
                ref={posterVideoEl}
              />
            </div>
          )
        }

        {
          // Image poster
          component.posterImage && !videoIsPlaying && (
            <img
              className="video__poster video__poster--image lazy"
              data-normal={formatImageUrl(posterImage, { width: 768 })}
              //src={component.posterImage+'?w=768&crop=fit 768w'}
            />
          )
        }

        {
          // Play button
          component.video && !component.autoplay && !videoIsPlaying && (
            <button
              className="video__play btn"
              onClick={() => {
                togglePlay();
              }}
            >
              <div className={'icon icon-play'}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 45 40">
                  <path
                    transform="translate(5 0)"
                    d="M39 18.2L3 .2C1.5-.5 0 .5 0 2v36c0 1.5 1.6 2.5 3 1.8l36-18c1.4-.7 1.4-2.8 0-3.6"
                    stroke="none"
                    fillRule="evenodd"
                  />
                </svg>
              </div>
            </button>
          )
        }
      </div>
      {component.caption && (
        <Caption
          component={{
            style: fullBleed ? 'full' : '',
          }}
        >
          {component.caption}
        </Caption>
      )}
      {children}
    </AnimateIn>
  );
};

export default VimeoVideo;
